import filtersGroupArrayValidationSchema from '../validation/filters.schema';
import * as yup from 'yup';
import { intervalSchema, timeTableDaySchema, timeTableUniqueTimeSchema } from '../validation/timeTable.schema';
import { tasksArraySchema } from '../validation/tasks.schema';
import { isEqual } from 'lodash-es';
import { toJS } from 'mobx';
import useFilterStore from '../hooks/useFilterStore';
import { useCurrentTasksFormStore } from '../hooks';
import useTimetableStore from '../hooks/useTimetableStore';
import type { GoogleRuleDetails } from '../google/types';
import { useCallback } from 'react';
import { useCurrentNetwork, useEnqueueSnackbar } from 'hooks';
import type { LevelValue, RuleEntry } from '../types';

const highlightError = (selector: string) => {
  const searchedElement = document.querySelector(selector);

  if (searchedElement) {
    searchedElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    searchedElement.setAttribute('style', 'border: 2px solid red;');
    setTimeout(() => {
      searchedElement.removeAttribute('style');
    }, 3000);
  }
};
export const useValidateFilters = () => {
  const filterStore = useFilterStore();
  const network = useCurrentNetwork();
  const { onShowInfoAlert } = useEnqueueSnackbar();

  return async () => {
    try {
      await filtersGroupArrayValidationSchema.validate(filterStore.filterGroups, { context: { network } });
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const [filterGroupIndex, filterIndex] = String(error.path)
          .split('')
          .filter((el) => !Number.isNaN(Number(el)));

        if (!filterGroupIndex || !filterIndex) {
          onShowInfoAlert(error.message);
          highlightError('#filters');
        } else {
          highlightError(`#fg-${filterGroupIndex} #filter-${filterIndex} div`);
        }
      }
      return false;
    }

    return true;
  };
};

export const useValidateSchedule = () => {
  const timetableStore = useTimetableStore();
  const { onShowInfoAlert } = useEnqueueSnackbar();

  return async () => {
    try {
      if (timetableStore.scheduleInterval) {
        await intervalSchema.validate(timetableStore.intervalDTO);
      } else {
        const timeArray = timetableStore.reversedTimetableEntries.map(([k, v]) => v.time);
        const daysArray = timetableStore.reversedTimetableEntries.map(([k, v]) => v.days);

        await timeTableUniqueTimeSchema.validate(timeArray);
        await timeTableDaySchema.validate(daysArray);
      }
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        if (timetableStore.scheduleInterval || !error?.path || !String(error.path).length) {
          onShowInfoAlert(error.message);
          highlightError('#timetable');
        } else {
          const index = String(error.path)[1];
          highlightError(`#time-row-${index}`);
        }
      }

      return false;
    }

    return true;
  };
};

export const useValidateTasks = (ruleLevel?: LevelValue) => {
  const tasksStore = useCurrentTasksFormStore();
  const { onShowInfoAlert } = useEnqueueSnackbar();
  const network = useCurrentNetwork();

  return async () => {
    try {
      const castedValue = tasksArraySchema.cast(tasksStore.tasksFormValues);
      await tasksArraySchema.validate(castedValue, { context: { ruleLevel, network } });
    } catch (error) {
      console.log(error);
      if (error instanceof yup.ValidationError) {
        if (String(error.path).includes('condition')) {
          const [task, condition] = String(error.path)
            .split('')
            .filter((el) => !Number.isNaN(Number(el)));

          highlightError(`#task-item-${task} #condition-${condition}`);
        } else if (String(error.path).includes('actions')) {
          const [task, action] = String(error.path)
            .split('')
            .filter((el) => !Number.isNaN(Number(el)));

          highlightError(`#task-item-${task} #action-${action}`);
        } else {
          highlightError('#tasks');
        }
        onShowInfoAlert(error.message);
      }
      return false;
    }

    return true;
  };
};

interface RuleChangesProps {
  rule: RuleEntry | GoogleRuleDetails;
  isBasicFormDirty: boolean;
}

export const useCheckRuleChanges = () => {
  const filterStore = useFilterStore();
  const taskStore = useCurrentTasksFormStore();
  const timetableStore = useTimetableStore();
  const { onShowInfoAlert } = useEnqueueSnackbar();

  return ({ rule, isBasicFormDirty }: RuleChangesProps) => {
    const timeTableObj = timetableStore.scheduleInterval ? timetableStore.intervalDTO : timetableStore.timeTableDTO;
    const ruleTimeTableObj = rule.data.timetable ? rule.data.timetable : rule.data.schedule_interval;

    const isEqualStateForm = [
      isEqual(rule.data.filters, toJS(filterStore.filterGroups)),
      isEqual(rule.data.tasks, toJS(taskStore.tasksFormValues)),
      isEqual(ruleTimeTableObj, toJS(timeTableObj)),
    ];

    if (isBasicFormDirty || isEqualStateForm.some((el) => !el)) {
      return true;
    }

    onShowInfoAlert('Please, change rule content before saving');
    return false;
  };
};

interface UseValidateProps {
  isDirty: boolean;
  //required for FB only
  ruleLevel?: LevelValue;
}

export const useValidate = ({ isDirty, ruleLevel }: UseValidateProps) => {
  const checkRuleChanges = useCheckRuleChanges();
  const validateFilters = useValidateFilters();
  const validateTasks = useValidateTasks(ruleLevel);
  const validateSchedule = useValidateSchedule();

  const validate = useCallback(
    async (rule: RuleEntry | GoogleRuleDetails) => {
      const ruleChanges = checkRuleChanges({ rule, isBasicFormDirty: isDirty });
      if (!ruleChanges) return false;

      const filtersValidation = await validateFilters();
      if (!filtersValidation) return false;

      const tasksValidation = await validateTasks();
      if (!tasksValidation) return false;

      const scheduleValidation = await validateSchedule();
      if (!scheduleValidation) return false;

      return true;
    },
    [checkRuleChanges, isDirty, validateFilters, validateSchedule, validateTasks]
  );

  return validate;
};
